import * as React from 'react'
import {
  Theme,
  createStyles,
  withStyles,
  WithStyles
} from '@material-ui/core/styles'
import {
  IIncidentPersonFields,
  IIncidentTypePersonFieldsConfiguration,
  IIncident
} from '../../../models/Incident'
import Grid from '@material-ui/core/Grid'
import { TextField, Typography } from '@material-ui/core'
import PersonIcon from '@material-ui/icons/Person'
import { CustomFieldsGroup } from '../../custom_fields/CustomFieldsGroup'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../../OneplaceLibraryProvider'
import { IPerson } from '../../../models/Person'

const styles = (_theme: Theme) => createStyles({})

export interface IIncidentPersonFieldsProps
  extends WithStyles<typeof styles>,
    IOneplaceLibraryContextProp {
  configuration: IIncidentTypePersonFieldsConfiguration
  incident: IIncident
}

export default withStyles(styles)(
  withOneplaceLibraryContext((props: IIncidentPersonFieldsProps) => {
    const [personFields, setPersonfieds] =
      React.useState<IIncidentPersonFields | null>()
    const [configuration, setConfiguration] =
      React.useState<IIncidentTypePersonFieldsConfiguration>({
        ...props.configuration
      })

    let hasConfigurationBeenChanged = false
    if (
      (!configuration && props.configuration) ||
      (configuration && !props.configuration) ||
      (configuration &&
        props.configuration &&
        configuration.id !== props.configuration.id)
    ) {
      setConfiguration(
        props.configuration ? { ...props.configuration } : props.configuration
      )
      hasConfigurationBeenChanged = true
    }

    // set person fields when editing incident
    if (
      !personFields &&
      props.incident.personFields &&
      props.incident.personFields.id
    ) {
      setPersonfieds(props.incident.personFields)
    }
    //check if there's been any change in person type or selected person (creating or editing)
    else {
      // type has configuration
      if (props.configuration && props.configuration.anyFieldSelected) {
        const hasPeopleSelected =
          props.incident.people && props.incident.people.length > 0
        const firstPerson = hasPeopleSelected ? props.incident.people![0] : null

        const isFirstSelection = !personFields && hasPeopleSelected //first time we're rending this component

        const hasFirstPersonChanged =
          personFields &&
          firstPerson &&
          firstPerson.id !== personFields.personId

        const hasCustomFieldsBeenUpdated =
          personFields &&
          !personFields.customFields &&
          firstPerson &&
          firstPerson.customFields &&
          firstPerson.customFields.length > 0 //custom fields have been fetched, we need to update component

        const hasAllPeopleBeenRemoved = personFields && !hasPeopleSelected

        if (
          isFirstSelection ||
          hasFirstPersonChanged ||
          hasCustomFieldsBeenUpdated ||
          (hasPeopleSelected && hasConfigurationBeenChanged)
        ) {
          const newPersonFields = createNewPersonFields(firstPerson!)
          setPersonfieds(newPersonFields)
          props.incident.personFields = newPersonFields // TODO add event and set on parent
        } else if (hasAllPeopleBeenRemoved && personFields) {
          setPersonfieds(null)
          props.incident.personFields = null
        }
      }
      // type has no configuration or have configuration with no fields to be displayed
      else if (personFields) {
        setPersonfieds(null)
        props.incident.personFields = null
      }
    }

    function createNewPersonFields(person: IPerson): IIncidentPersonFields {
      const newPersonFields = {} as IIncidentPersonFields
      newPersonFields.personId = person.id

      if (props.configuration.firstName)
        newPersonFields.firstName = person.firstName || ''
      if (props.configuration.midName)
        newPersonFields.midName = person.midName || ''
      if (props.configuration.lastName)
        newPersonFields.lastName = person.lastName || ''
      if (props.configuration.email)
        newPersonFields.email = person.emailAdd || ''
      if (props.configuration.homePhoneNumber)
        newPersonFields.homePhoneNumber = person.contact?.homePhone || ''
      if (props.configuration.mobileNumber)
        newPersonFields.mobileNumber = person.contact?.mobilePhone || ''
      if (props.configuration.workPhoneNumber)
        newPersonFields.workPhoneNumber = person.contact?.workPhone || ''
      if (props.configuration.externalId)
        newPersonFields.externalId = person.externalId || ''

      if (
        props.configuration.customFieldsJson &&
        props.configuration.customFieldsJson.length > 0 &&
        person.customFields &&
        person.customFields.length > 0
      ) {
        const cfToBeDisplayed = props.configuration.customFieldsJson.map(
          (cf) => cf.customFieldId
        )
        const personCFToBeDisplayed = []
        // cfToBeDisplayed is ordered (CF order)
        for (const cfId of cfToBeDisplayed) {
          const personCF = person.customFields.find(
            (pcf) => pcf.customFieldId === cfId
          )
          if (personCF) personCFToBeDisplayed.push(personCF)
        }
        newPersonFields.customFields = personCFToBeDisplayed
      }
      return newPersonFields
    }

    return (
      <>
        {personFields && (
          <Grid container spacing={0} style={{ marginBottom: 16 }}>
            <Grid item xs={12}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap'
                }}
              >
                <PersonIcon style={{ fontSize: '32px', marginRight: '5px' }} />
                <Typography variant='h5'>Person</Typography>
              </div>
            </Grid>
            {personFields.firstName != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='First Name'
                  value={personFields.firstName}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.midName != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Mid Name'
                  value={personFields.midName}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.lastName != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Last Name'
                  value={personFields.lastName}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.email != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Email'
                  value={personFields.email}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.workPhoneNumber != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Work Phone Number'
                  value={personFields.workPhoneNumber}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.homePhoneNumber != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Home Phone Number'
                  value={personFields.homePhoneNumber}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.mobileNumber != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Mobile Number'
                  value={personFields.mobileNumber}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.externalId != null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label='External ID'
                  value={personFields.externalId}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {personFields.customFields && (
              <CustomFieldsGroup
                key={personFields.personId} // force render when changing person
                label=''
                fields={personFields.customFields.map((cf) => {
                  cf.readonly = true
                  return cf
                })}
                helpInfos={[]}
                onFieldChanged={() => null}
              />
            )}
          </Grid>
        )}
      </>
    )
  })
)
